import React from 'react'

import { NotFound } from 'v2/ui/svgs/list-view-empty-states/NotFound'
import { ServerError } from 'v2/ui/svgs/list-view-empty-states/ServerError'

import { Box } from 'ui/components/Box'
import { Button } from 'ui/components/Button'
import { Body, Headline } from 'ui/components/Text'

type DetailViewBaseEmptyStateProps = React.ComponentPropsWithoutRef<typeof Box> & {
    image?: React.ReactNode
    title?: string
    description?: string
    footer?: React.ReactNode
}

export const DetailViewBaseEmptyState: React.FC<DetailViewBaseEmptyStateProps> = ({
    image,
    title,
    description,
    footer,
    ...rest
}) => {
    return (
        <Box
            flex
            center
            height="full"
            width="full"
            alignSelf="stretch"
            grow
            shrink
            justifyContent="center"
            {...rest}
        >
            <Box flex center flexDirection="column" gap="3xl" py="4xl" px="m" {...rest}>
                {image}
                {(title || description) && (
                    <Box
                        flex
                        center
                        flexDirection="column"
                        textAlign="center"
                        justifyContent="center"
                        gap="xl"
                        px="xl"
                    >
                        {title && (
                            <Box>
                                <Headline size="l" color="text">
                                    {title}
                                </Headline>
                            </Box>
                        )}
                        {description && (
                            <Box maxWidth="400px">
                                <Body size="m" color="textWeaker">
                                    {description}
                                </Body>
                            </Box>
                        )}
                    </Box>
                )}
                {footer && (
                    <Box flex center justifyContent="center">
                        {footer}
                    </Box>
                )}
            </Box>
        </Box>
    )
}

type DetailViewServerErrorEmptyStateProps = React.ComponentPropsWithoutRef<
    typeof DetailViewBaseEmptyState
> & {
    onRetry?: () => void
}

export const DetailViewServerErrorEmptyState: React.FC<DetailViewServerErrorEmptyStateProps> =
    React.memo(function DetailViewServerErrorEmptyState({ onRetry, ...props }) {
        return (
            <DetailViewBaseEmptyState
                image={<ServerError />}
                title="Something's wrong"
                description="We were unable to connect to the service. Click Retry to try again."
                footer={
                    onRetry && (
                        <Button variant="primary" size="m" onClick={onRetry}>
                            Retry
                        </Button>
                    )
                }
                {...props}
            />
        )
    })

type DetailViewNotFoundEmptyStateProps = React.ComponentPropsWithoutRef<
    typeof DetailViewBaseEmptyState
> & {}

export const DetailViewNotFoundEmptyState: React.FC<DetailViewNotFoundEmptyStateProps> = React.memo(
    function DetailViewNotFoundEmptyState(props) {
        return (
            <DetailViewBaseEmptyState
                image={<NotFound />}
                title="Not found"
                description="The record you are looking for does not exist."
                {...props}
            />
        )
    }
)
